import { useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import EmptyState from "@/pages/admin/dashboard/components/EmptyState";
import { timeAgo } from "@/pages/admin/dashboard/types";
import type { AdminProfile } from "@/pages/admin/dashboard/types";
import type { Permission, RolePermission } from "@/pages/super-admin/page";

interface AdminsTabProps {
  profiles: AdminProfile[];
  permissions: Permission[];
  rolePermissions: RolePermission[];
  onRefresh: () => void;
}

function initials(name: string | null, email: string | null): string {
  return (name ?? email ?? "U").charAt(0).toUpperCase();
}

export default function AdminsTab({
  profiles,
  permissions,
  rolePermissions,
  onRefresh,
}: AdminsTabProps) {
  const { showToast } = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [newAdminId, setNewAdminId] = useState<string>("");

  const admins = useMemo(
    () => profiles.filter((p) => p.role === "admin"),
    [profiles]
  );

  const candidates = useMemo(
    () => profiles.filter((p) => p.role === "user"),
    [profiles]
  );

  const adminPermissionIds = useMemo(() => {
    const set = new Set<string>();
    rolePermissions
      .filter((rp) => rp.role === "admin")
      .forEach((rp) => set.add(rp.permission_id));
    return set;
  }, [rolePermissions]);

  const createAdmin = async () => {
    if (!newAdminId) return;
    setBusyId(newAdminId);
    const { error } = await supabase.rpc("create_admin", {
      target_uuid: newAdminId,
    });
    setBusyId(null);
    if (error) {
      showToast(error.message || "No se pudo crear el administrador.", "error");
      return;
    }
    showToast("Administrador creado correctamente.", "success");
    setNewAdminId("");
    onRefresh();
  };

  const removeAdmin = async (admin: AdminProfile) => {
    setBusyId(admin.id);
    const { error } = await supabase.rpc("remove_admin", {
      target_uuid: admin.id,
    });
    setBusyId(null);
    if (error) {
      showToast(error.message || "No se pudo revocar el administrador.", "error");
      return;
    }
    showToast("Administrador revocado.", "success");
    onRefresh();
  };

  const togglePermission = async (permission: Permission) => {
    const granted = adminPermissionIds.has(permission.id);
    const { error } = await supabase.rpc(
      granted ? "revoke_permission" : "grant_permission",
      {
        p_role: "admin",
        perm_name: permission.name,
      }
    );
    if (error) {
      showToast(error.message || "No se pudo actualizar el permiso.", "error");
      return;
    }
    showToast(
      granted
        ? `Permiso «${permission.name}» revocado.`
        : `Permiso «${permission.name}» otorgado.`,
      "success"
    );
    onRefresh();
  };

  return (
    <div className="space-y-8 p-5">
      {/* Crear administrador */}
      <section>
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-900">
          <i className="ri-user-add-line text-base text-primary-500"></i>
          Crear administrador
        </h4>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select
            value={newAdminId}
            onChange={(e) => setNewAdminId(e.target.value)}
            className="w-full cursor-pointer rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 sm:max-w-xs"
          >
            <option value="">Selecciona un usuario…</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name ?? c.email ?? "Usuario"} ({c.email})
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!newAdminId || busyId === newAdminId}
            onClick={createAdmin}
            className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className="ri-shield-star-line"></i>
            Crear administrador
          </button>
        </div>
        <p className="mt-2 text-xs text-foreground-500">
          Solo puedes promover usuarios existentes a «administrador». No puedes
          crear un super admin desde aquí.
        </p>
      </section>

      {/* Lista de administradores */}
      <section>
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-900">
          <i className="ri-shield-star-line text-base text-primary-500"></i>
          Administradores ({admins.length})
        </h4>
        {admins.length === 0 ? (
          <EmptyState
            icon="ri-shield-star-line"
            title="Sin administradores"
            description="Aún no has creado ningún administrador."
          />
        ) : (
          <div className="divide-y divide-background-200/60 rounded-lg border border-background-200/70">
            {admins.map((admin) => (
              <div
                key={admin.id}
                className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
                  {initials(admin.full_name, admin.email)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground-950">
                    {admin.full_name ?? "Administrador"}
                  </p>
                  <p className="truncate text-xs text-foreground-500">
                    {admin.email}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={busyId === admin.id}
                  onClick={() => removeAdmin(admin)}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-200 px-3 py-1.5 text-xs font-semibold text-accent-700 transition-colors hover:bg-accent-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-user-unfollow-line"></i>
                  Revocar admin
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Permisos del rol admin */}
      <section>
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-900">
          <i className="ri-key-2-line text-base text-primary-500"></i>
          Permisos del rol «admin»
        </h4>
        <p className="mb-3 text-xs text-foreground-500">
          Estos permisos se aplican a todos los administradores. El super admin
          siempre tiene acceso total.
        </p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {permissions.map((permission) => {
            const granted = adminPermissionIds.has(permission.id);
            return (
              <button
                key={permission.id}
                type="button"
                onClick={() => togglePermission(permission)}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                  granted
                    ? "border-secondary-300 bg-secondary-50"
                    : "border-background-200 bg-background-50 hover:bg-background-100"
                }`}
              >
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${
                    granted ? "bg-secondary-500 text-white" : "border border-background-300"
                  }`}
                >
                  {granted && <i className="ri-check-line text-sm"></i>}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-900">
                    {permission.name}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    {permission.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <p className="text-xs text-foreground-400">
        Última actualización de permisos registrada en la auditoría.
      </p>
    </div>
  );
}